<template>
  <div class="user-management">
    <el-card shadow="never" class="main-card animate__animated animate__fadeInUp">
      <!-- 搜索栏 + 累计收益（同一行） -->
      <div class="toolbar-row">
        <div class="search-group">
          <el-input
            v-model="searchUserId"
            placeholder="用户ID"
            clearable
            size="small"
            style="width: 140px;"
            @keyup.enter.native="handleSearch"
            @clear="handleSearch"
          />
          <el-input
            v-model="searchNickname"
            placeholder="搜索昵称"
            prefix-icon="el-icon-search"
            clearable
            size="small"
            style="width: 180px;"
            @keyup.enter.native="handleSearch"
            @clear="handleSearch"
          />
          <el-input
            v-model="searchInviteCode"
            placeholder="邀请码"
            prefix-icon="el-icon-collection-tag"
            clearable
            size="small"
            style="width: 160px;"
            @keyup.enter.native="handleSearch"
            @clear="handleSearch"
          />
          <el-select
            v-model="searchStatus"
            placeholder="用户状态"
            clearable
            size="small"
            style="width: 130px;"
            @change="handleSearch"
          >
            <el-option label="全部" :value="''" />
            <el-option label="未封禁" :value="0" />
            <el-option label="已封禁" :value="1" />
          </el-select>
          <el-select
            v-model="searchRiskLevel"
            placeholder="风险等级"
            clearable
            size="small"
            style="width: 130px;"
            @change="handleSearch"
          >
            <el-option label="全部风险等级" :value="''" />
            <el-option label="无风险" :value="0" />
            <el-option label="低风险" :value="1" />
            <el-option label="中风险" :value="2" />
            <el-option label="高风险" :value="3" />
          </el-select>
          <el-select
            v-model="searchDeviceRisk"
            placeholder="设备风险"
            clearable
            size="small"
            style="width: 130px;"
            @change="handleSearch"
          >
            <el-option label="全部设备" :value="''" />
            <el-option label="正常设备" :value="0" />
            <el-option label="风险设备" :value="1" />
          </el-select>
          <el-button type="primary" size="small" icon="el-icon-search" @click="handleSearch">搜索</el-button>
          <el-button size="small" icon="el-icon-refresh" @click="refreshList">刷新</el-button>
        </div>
        <div class="earned-summary">
          <i class="el-icon-coin"></i>
          <span class="earned-label">累计总收益</span>
          <span class="earned-value">{{ totalEarned }}</span>
          <span class="earned-unit">金币</span>
        </div>
      </div>

      <div v-if="error" class="error-msg">{{ error }}</div>
      <el-empty
        v-else-if="!loading && users.length === 0"
        :description="searchUserId || searchNickname || searchInviteCode ? '没有符合条件的用户' : '暂无用户数据'"
      />

      <div v-else class="table-wrapper">
        <el-table
          :data="users"
          border
          stripe
          v-loading="loading"
          element-loading-text="正在加载..."
          class="data-table"
          row-key="id"
          :header-cell-style="{ background: '#fafafa', color: '#303133', fontWeight: 600 }"
        >
          <el-table-column prop="id" label="ID" width="80" align="center" />
          <el-table-column prop="openid" label="OpenID" min-width="160" show-overflow-tooltip />
          <el-table-column prop="nickname" label="昵称" min-width="140" show-overflow-tooltip />
          <el-table-column prop="avatarUrl" label="头像" width="80" align="center">
            <template slot-scope="scope">
              <el-avatar v-if="scope.row.avatarUrl" :key="scope.row.id" :src="scope.row.avatarUrl" :size="32" shape="circle" />
              <span v-else class="text-muted">-</span>
            </template>
          </el-table-column>
          <el-table-column prop="inviteCode" label="邀请码" width="100" align="center">
            <template slot-scope="scope">
              <el-tag size="mini" effect="plain">{{ scope.row.inviteCode || '-' }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="leaderId" label="代理ID" width="80" align="center" />
          <el-table-column prop="parentId" label="上级ID" width="80" align="center" />
          <el-table-column prop="status" label="状态" width="80" align="center">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.status === 0" type="success" size="small" effect="plain">正常</el-tag>
              <el-tag v-else-if="scope.row.status === 1" type="danger" size="small" effect="plain">封禁</el-tag>
              <span v-else class="text-muted">-</span>
            </template>
          </el-table-column>
          <el-table-column prop="riskLevel" label="用户风险" width="80" align="center">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.riskLevel === 1" type="success" size="small">低</el-tag>
              <el-tag v-else-if="scope.row.riskLevel === 2" type="warning" size="small">中</el-tag>
              <el-tag v-else-if="scope.row.riskLevel === 3" type="danger" size="small">高</el-tag>
              <span v-else class="text-muted">-</span>
            </template>
          </el-table-column>
          <el-table-column prop="deviceRisk" label="设备风险" width="90" align="center">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.deviceRisk === 0" type="success" size="small" effect="plain">正常</el-tag>
              <el-tag v-else-if="scope.row.deviceRisk === 1" type="danger" size="small" effect="plain">风险</el-tag>
              <span v-else class="text-muted">-</span>
            </template>
          </el-table-column>
          <el-table-column prop="createdAt" label="注册时间" width="160" align="center" />
          <el-table-column label="操作" width="320" align="center" fixed="right">
            <template slot-scope="scope">
              <el-button
                size="mini"
                type="primary"
                plain
                icon="el-icon-view"
                @click="showDetail(scope.row)"
              >
                详情
              </el-button>
              <el-button
                v-if="canEdit"
                size="mini"
                type="warning"
                plain
                @click="openBalanceDialog(scope.row)"
              >
                改余额
              </el-button>
              <el-button
                v-if="canEdit"
                size="mini"
                type="info"
                plain
                @click="openParentDialog(scope.row)"
              >
                改上级
              </el-button>
              <el-button
                v-if="canEdit"
                size="mini"
                :type="scope.row.status === 0 ? 'danger' : 'success'"
                plain
                @click="handleToggleStatus(scope.row)"
              >
                {{ scope.row.status === 0 ? '禁用' : '启用' }}
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination-wrapper">
          <el-pagination
            background
            layout="total, sizes, prev, pager, next, jumper"
            :current-page="pagination.page"
            :page-size="pagination.pageSize"
            :page-sizes="[10, 20, 50, 100]"
            :total="pagination.total"
            @current-change="handleCurrentChange"
            @size-change="handleSizeChange"
          />
        </div>
      </div>
    </el-card>

    <!-- 用户详情弹窗 -->
    <el-dialog
      title="用户详情"
      :visible.sync="detailDialogVisible"
      width="440px"
      :close-on-click-modal="false"
      center
      append-to-body
    >
      <div v-loading="detailLoading" element-loading-text="加载中..." class="detail-content">
        <div class="detail-header">
          <el-avatar v-if="detailUser.avatarUrl" :src="detailUser.avatarUrl" :size="56" shape="circle" />
          <div class="detail-name">{{ detailUser.nickname || '-' }}</div>
          <div class="detail-id">ID: {{ detailUser.id }}</div>
        </div>
        <div class="detail-cards">
          <div class="detail-card coin-current">
            <div class="detail-card-label">当前余额</div>
            <div class="detail-card-value">{{ detailUser.coinBalance || 0 }}</div>
            <div class="detail-card-unit">金币</div>
          </div>
          <div class="detail-card coin-total">
            <div class="detail-card-label">累计余额</div>
            <div class="detail-card-value">{{ detailUser.totalEarnedCoins || 0 }}</div>
            <div class="detail-card-unit">金币</div>
          </div>
        </div>
        <div v-if="detailUser.status === 1 && detailUser.banDes" class="detail-ban-reason">
          <div class="ban-reason-label">封禁原因</div>
          <div class="ban-reason-text">{{ detailUser.banDes }}</div>
        </div>
        <!-- 设备风险详情 -->
        <div v-if="detailUser.deviceRisk !== null && detailUser.deviceRisk !== undefined" class="detail-risk-section">
          <div class="risk-section-title">
            <i class="el-icon-warning-outline"></i>
            设备安全检测
            <el-tag :type="detailUser.deviceRisk === 1 ? 'danger' : 'success'" size="mini" style="margin-left:8px;">
              {{ detailUser.deviceRisk === 1 ? '风险设备' : '正常设备' }}
            </el-tag>
          </div>
          <div v-if="parsedRiskDetail && parsedRiskDetail.length > 0" class="risk-items">
            <div
              v-for="(item, index) in parsedRiskDetail"
              :key="index"
              class="risk-item"
            >
              <div class="risk-item-header">
                <span class="risk-item-label">{{ item.label }}</span>
                <el-tag
                  :type="item.isRisk ? 'danger' : 'success'"
                  size="mini"
                  effect="plain"
                >
                  {{ item.isRisk ? '命中' : '正常' }}
                </el-tag>
              </div>
              <el-tooltip v-if="item.desc" :content="item.desc" placement="top-start" effect="dark">
                <span class="risk-item-desc">{{ item.desc }}</span>
              </el-tooltip>
            </div>
          </div>
          <div v-if="detailUser.deviceRiskDetail" class="risk-raw-json">
            <div class="risk-raw-title" @click="showRawJson = !showRawJson">
              <i :class="showRawJson ? 'el-icon-arrow-down' : 'el-icon-arrow-right'"></i>
              原始风险详情 JSON
            </div>
            <pre v-if="showRawJson" class="risk-json-content">{{ formattedRiskJson }}</pre>
          </div>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="detailDialogVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <!-- 封禁用户弹窗 -->
    <el-dialog
      title="封禁用户"
      :visible.sync="banDialogVisible"
      width="440px"
      :close-on-click-modal="false"
      center
      append-to-body
    >
      <el-form ref="banFormRef" :model="banForm" :rules="banRules" label-width="100px">
        <el-form-item label="用户">
          <el-input :value="banUser ? banUser.nickname || banUser.id : ''" disabled />
        </el-form-item>
        <el-form-item label="封禁描述" prop="banDes">
          <el-input
            v-model.trim="banForm.banDes"
            type="textarea"
            :rows="3"
            maxlength="200"
            show-word-limit
            placeholder="请输入封禁原因"
          />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="banDialogVisible = false">取消</el-button>
        <el-button type="danger" @click="handleBanSubmit">确认封禁</el-button>
      </div>
    </el-dialog>

    <!-- 修改余额弹窗 -->
    <el-dialog
      title="修改用户余额"
      :visible.sync="balanceDialogVisible"
      width="440px"
      :close-on-click-modal="false"
      center
      append-to-body
    >
      <el-form ref="balanceFormRef" :model="balanceForm" :rules="balanceRules" label-width="100px" v-loading="balanceLoading">
        <el-form-item label="用户">
          <el-input :value="balanceTarget ? ((balanceTarget.nickname || '-') + ' (ID:' + balanceTarget.id + ')') : ''" disabled />
        </el-form-item>
        <el-form-item label="当前余额">
          <el-input :value="balanceForm.currentBalance + ' 金币'" disabled />
        </el-form-item>
        <el-form-item label="目标余额" prop="coinBalance">
          <el-input-number
            v-model="balanceForm.coinBalance"
            :min="0"
            :step="1"
            :precision="0"
            controls-position="right"
            style="width: 100%;"
          />
        </el-form-item>
        <el-form-item label="备注">
          <el-input
            v-model.trim="balanceForm.remark"
            type="textarea"
            :rows="2"
            maxlength="100"
            show-word-limit
            placeholder="可选，写入金币流水"
          />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="balanceDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="balanceSubmitting" @click="handleBalanceSubmit">确认修改</el-button>
      </div>
    </el-dialog>

    <!-- 修改上级弹窗 -->
    <el-dialog
      title="修改上级ID"
      :visible.sync="parentDialogVisible"
      width="440px"
      :close-on-click-modal="false"
      center
      append-to-body
    >
      <el-form ref="parentFormRef" :model="parentForm" :rules="parentRules" label-width="100px">
        <el-form-item label="用户">
          <el-input :value="parentTarget ? ((parentTarget.nickname || '-') + ' (ID:' + parentTarget.id + ')') : ''" disabled />
        </el-form-item>
        <el-form-item label="当前上级">
          <el-input :value="parentForm.currentParentId != null ? parentForm.currentParentId : '无'" disabled />
        </el-form-item>
        <el-form-item label="新上级ID" prop="parentId">
          <el-input
            v-model="parentForm.parentId"
            clearable
            placeholder="填写用户ID；清空并勾选下方可解除上级"
          />
        </el-form-item>
        <el-form-item label="清空上级">
          <el-checkbox v-model="parentForm.clearParentId">解除上级绑定（parent_id 置空）</el-checkbox>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="parentDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="parentSubmitting" @click="handleParentSubmit">确认修改</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { get, put } from '@/utils/http'

export default {
  name: 'UserManagement',
  data() {
    return {
      users: [],
      loading: false,
      error: '',
      searchUserId: '',
      searchNickname: '',
      searchInviteCode: '',
      searchStatus: '',
      searchRiskLevel: '',
      searchDeviceRisk: '',
      totalEarned: 0,
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      detailDialogVisible: false,
      detailLoading: false,
      detailUser: {},
      banDialogVisible: false,
      banUser: null,
      banForm: {
        status: 1,
        banDes: ''
      },
      banRules: {
        banDes: [{ required: true, message: '请输入封禁描述', trigger: 'blur' }]
      },
      balanceDialogVisible: false,
      balanceLoading: false,
      balanceSubmitting: false,
      balanceTarget: null,
      balanceForm: {
        currentBalance: 0,
        coinBalance: 0,
        remark: ''
      },
      balanceRules: {
        coinBalance: [{ required: true, message: '请输入目标余额', trigger: 'change' }]
      },
      parentDialogVisible: false,
      parentSubmitting: false,
      parentTarget: null,
      parentForm: {
        currentParentId: null,
        parentId: '',
        clearParentId: false
      },
      parentRules: {},
      showRawJson: false,
      riskTagDict: {}
    }
  },
  computed: {
    // 是否有修改权限（仅超级管理员 role=0）
    canEdit() {
      return parseInt(localStorage.getItem('role')) === 0
    },
    // 解析设备风险详情 JSON，返回可读的风险项列表
    // 风险标签中文释义来自后台 device_risk_tags
    parsedRiskDetail() {
      if (!this.detailUser.deviceRiskDetail) return []
      let raw = this.detailUser.deviceRiskDetail
      try {
        if (typeof raw === 'string') {
          raw = JSON.parse(raw)
        }
      } catch {
        // 解析失败，当作纯字符串处理
        raw = { tags: raw }
      }

      // 提取标签列表：支持 { tags: 'a,b' }、直接对象、纯字符串
      let tagsArr = []
      if (raw && typeof raw === 'object') {
        if (raw.tags !== undefined && typeof raw.tags === 'string') {
          tagsArr = raw.tags.split(',').filter(Boolean)
        } else if (raw.tags !== undefined && Array.isArray(raw.tags)) {
          tagsArr = raw.tags.filter(Boolean)
        } else {
          // 遍历对象 key，值为 true/1/非空的视为命中
          tagsArr = Object.keys(raw).filter(key => {
            if (key === 'tags' || key === 'deviceRisk' || key === 'deviceRiskDetail') return false
            const val = raw[key]
            return val === true || val === 1 || val === '1' || (typeof val === 'string' && val.trim() !== '')
          })
        }
      }

      // 阿里云风险标签释义来自后台 device_risk_tags
      const items = []
      tagsArr.forEach(key => {
        const cleanKey = key.trim()
        const info = this.riskTagDict[cleanKey]
        if (info) {
          items.push({
            key: cleanKey,
            label: info.label,
            isRisk: true,
            desc: info.desc
          })
        } else {
          items.push({
            key: cleanKey,
            label: cleanKey,
            isRisk: true,
            desc: '未知风险标签'
          })
        }
      })
      return items
    },
    // 格式化后的风险 JSON
    formattedRiskJson() {
      if (!this.detailUser.deviceRiskDetail) return ''
      try {
        const obj = typeof this.detailUser.deviceRiskDetail === 'string'
          ? JSON.parse(this.detailUser.deviceRiskDetail)
          : this.detailUser.deviceRiskDetail
        return JSON.stringify(obj, null, 2)
      } catch {
        return this.detailUser.deviceRiskDetail
      }
    }
  },
  mounted() {
    this.fetchUsers()
    this.fetchTotalEarned()
    this.fetchRiskTagDict()
  },
  methods: {
    async fetchRiskTagDict() {
      try {
        const result = await get('/admin/risk/aliyun-tags/dict')
        const list = result && Array.isArray(result.data) ? result.data : []
        const map = {}
        list.forEach((tag) => {
          if (!tag || !tag.tagState) return
          map[tag.tagState] = {
            label: tag.tagMean || tag.tagState,
            desc: tag.tagDesc || ''
          }
        })
        this.riskTagDict = map
      } catch (e) {
        console.error('获取阿里云风险标签字典失败:', e && e.message)
      }
    },
    async fetchTotalEarned() {
      try {
        const result = await get('/admin/users/total-earned')
        this.totalEarned = (result && result.data != null) ? result.data : 0
      } catch (e) {
        console.error('获取累计总收益失败:', e.message)
      }
    },

    async showDetail(user) {
      this.detailUser = {
        id: user.id,
        nickname: user.nickname,
        avatarUrl: user.avatarUrl
      }
      this.showRawJson = false
      this.detailDialogVisible = true
      this.detailLoading = true
      try {
        const result = await get(`/admin/users/${user.id}/balance`)
        const data = (result && result.data) ? result.data : {}
        this.detailUser = {
          id: data.id || user.id,
          nickname: data.nickname || user.nickname,
          avatarUrl: data.avatarUrl || user.avatarUrl,
          coinBalance: data.coinBalance,
          totalEarnedCoins: data.totalEarned,
          banDes: data.banDes || '',
          status: data.status !== undefined ? data.status : user.status,
          deviceRisk: data.deviceRisk !== undefined ? data.deviceRisk : user.deviceRisk,
          deviceRiskDetail: data.deviceRiskDetail || user.deviceRiskDetail || ''
        }
      } catch (e) {
        this.$message.error(e.message || '获取用户详情失败')
      } finally {
        this.detailLoading = false
      }
    },

    async fetchUsers() {
      this.loading = true
      this.error = ''
      try {
        const params = {
          page: this.pagination.page,
          pageSize: this.pagination.pageSize
        }
        if (this.searchUserId) {
          params.userId = this.searchUserId
        }
        if (this.searchNickname) {
          params.nickname = this.searchNickname
        }
        if (this.searchInviteCode) {
          params.inviteCode = this.searchInviteCode
        }
        if (this.searchStatus !== '' && this.searchStatus !== null) {
          params.status = this.searchStatus
        }
        if (this.searchRiskLevel !== '' && this.searchRiskLevel !== null) {
          params.riskLevel = this.searchRiskLevel
        }
        if (this.searchDeviceRisk !== '' && this.searchDeviceRisk !== null) {
          params.deviceRisk = this.searchDeviceRisk
        }

        const result = await get('/admin/users', params)
        const data = (result && result.data) ? result.data : {}
        this.users = Array.isArray(data.users) ? data.users : []
        this.pagination.total = data.total || 0
        this.pagination.pageSize = data.pageSize || this.pagination.pageSize
        this.pagination.page = data.page || this.pagination.page
      } catch (e) {
        this.error = e && e.message ? e.message : '获取用户列表失败'
        this.users = []
        this.pagination.total = 0
      } finally {
        this.loading = false
      }
    },

    handleSearch() {
      this.pagination.page = 1
      this.fetchUsers()
    },

    handleCurrentChange(page) {
      this.pagination.page = page
      this.fetchUsers()
    },

    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.page = 1
      this.fetchUsers()
    },

    refreshList() {
      this.searchUserId = ''
      this.searchNickname = ''
      this.searchInviteCode = ''
      this.searchStatus = ''
      this.searchRiskLevel = ''
      this.searchDeviceRisk = ''
      this.pagination.page = 1
      this.fetchUsers()
    },

    handleToggleStatus(user) {
      if (user.status === 0) {
        // 封禁用户：弹出对话框填写封禁描述
        this.banUser = user
        this.banForm.status = 1
        this.banForm.banDes = ''
        this.banDialogVisible = true
        this.$nextTick(() => {
          if (this.$refs.banFormRef) this.$refs.banFormRef.clearValidate()
        })
      } else {
        // 解封用户：直接发送
        this.doUpdateStatus(user, 0)
      }
    },
    async doUpdateStatus(user, status, banDes = null) {
      const actionText = status === 1 ? '封禁' : '解封'
      try {
        const payload = { status }
        if (banDes) payload.banDes = banDes
        const res = await put(`/admin/users/${user.id}`, payload)
        this.$message.success(res.msg || `${actionText}成功`)
        user.status = status
        if (status === 0) user.banDes = null
      } catch (e) {
        this.$message.error(e.message || `${actionText}失败`)
      }
    },
    async handleBanSubmit() {
      try {
        await this.$refs.banFormRef.validate()
      } catch {
        return
      }
      this.banDialogVisible = false
      await this.doUpdateStatus(this.banUser, this.banForm.status, this.banForm.banDes)
    },

    async openBalanceDialog(user) {
      this.balanceTarget = user
      this.balanceForm = {
        currentBalance: 0,
        coinBalance: 0,
        remark: ''
      }
      this.balanceDialogVisible = true
      this.balanceLoading = true
      try {
        const result = await get(`/admin/users/${user.id}/balance`)
        const data = (result && result.data) ? result.data : {}
        const current = data.coinBalance != null ? data.coinBalance : 0
        this.balanceForm.currentBalance = current
        this.balanceForm.coinBalance = current
      } catch (e) {
        this.$message.error(e.message || '获取余额失败')
        this.balanceDialogVisible = false
      } finally {
        this.balanceLoading = false
      }
    },

    async handleBalanceSubmit() {
      try {
        await this.$refs.balanceFormRef.validate()
      } catch {
        return
      }
      if (!this.balanceTarget) return
      const coinBalance = Number(this.balanceForm.coinBalance)
      if (!Number.isFinite(coinBalance) || coinBalance < 0 || !Number.isInteger(coinBalance)) {
        this.$message.warning('余额须为非负整数')
        return
      }
      this.balanceSubmitting = true
      try {
        const payload = { coinBalance }
        if (this.balanceForm.remark) payload.remark = this.balanceForm.remark
        const res = await put(`/admin/users/${this.balanceTarget.id}/balance`, payload)
        this.$message.success(res.msg || '余额修改成功')
        this.balanceDialogVisible = false
        this.fetchUsers()
        this.fetchTotalEarned()
      } catch (e) {
        this.$message.error(e.message || '余额修改失败')
      } finally {
        this.balanceSubmitting = false
      }
    },

    openParentDialog(user) {
      this.parentTarget = user
      this.parentForm = {
        currentParentId: user.parentId != null ? user.parentId : null,
        parentId: user.parentId != null ? String(user.parentId) : '',
        clearParentId: false
      }
      this.parentDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.parentFormRef) this.$refs.parentFormRef.clearValidate()
      })
    },

    async handleParentSubmit() {
      if (!this.parentTarget) return
      const payload = {}
      if (this.parentForm.clearParentId) {
        payload.clearParentId = true
      } else {
        const raw = (this.parentForm.parentId || '').toString().trim()
        if (!raw) {
          this.$message.warning('请填写新上级ID，或勾选清空上级')
          return
        }
        const parentId = Number(raw)
        if (!Number.isInteger(parentId) || parentId <= 0) {
          this.$message.warning('上级ID须为正整数')
          return
        }
        if (parentId === this.parentTarget.id) {
          this.$message.warning('上级不能是自己')
          return
        }
        payload.parentId = parentId
      }
      this.parentSubmitting = true
      try {
        const res = await put(`/admin/users/${this.parentTarget.id}`, payload)
        this.$message.success(res.msg || '上级修改成功')
        this.parentDialogVisible = false
        this.fetchUsers()
      } catch (e) {
        this.$message.error(e.message || '上级修改失败')
      } finally {
        this.parentSubmitting = false
      }
    }
  }
}
</script>

<style scoped>
.user-management {
  padding: 4px;
}

.main-card {
  border-radius: 8px;
}

/* ===== 工具栏行 ===== */
.toolbar-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px 20px;
  margin-bottom: 16px;
  background: #f8fafc;
  border-radius: 10px;
  border: 1px solid #e8ecf1;
}

.search-group {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  flex: 1;
}

/* ===== 累计收益 ===== */
.earned-summary {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  background: linear-gradient(135deg, #ecfdf5 0%, #f0f9ff 100%);
  border-radius: 8px;
  border: 1px solid #d1fae5;
  white-space: nowrap;
  flex-shrink: 0;
}

.earned-summary .el-icon-coin {
  font-size: 18px;
  color: #e6a23c;
  margin-right: 4px;
}

.earned-label {
  font-size: 13px;
  color: #606266;
  margin-right: 4px;
}

.earned-value {
  font-size: 20px;
  font-weight: 700;
  color: #059669;
  letter-spacing: 0.5px;
}

.earned-unit {
  font-size: 12px;
  color: #909399;
}

/* ===== 错误信息 ===== */
.error-msg {
  padding: 10px 16px;
  margin-bottom: 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #dc2626;
  font-size: 13px;
}

/* ===== 表格 ===== */
.table-wrapper {
  margin-top: 4px;
}

.data-table {
  border-radius: 6px;
  overflow: hidden;
}

.data-table::before {
  display: none;
}

.text-muted {
  color: #c0c4cc;
}

/* ===== 分页 ===== */
.pagination-wrapper {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
  padding: 8px 0;
}

/* ===== 详情弹窗 ===== */
.detail-content {
  padding: 8px 0;
}

.detail-header {
  text-align: center;
  padding: 12px 0 20px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 20px;
}

.detail-name {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  margin-top: 10px;
}

.detail-id {
  font-size: 13px;
  color: #909399;
  margin-top: 4px;
}

.detail-cards {
  display: flex;
  gap: 16px;
  padding: 0 4px;
}

.detail-card {
  flex: 1;
  text-align: center;
  padding: 20px 12px;
  border-radius: 10px;
  border: 1px solid;
}

.detail-card-label {
  font-size: 13px;
  color: #606266;
  margin-bottom: 8px;
}

.detail-card-value {
  font-size: 26px;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.detail-card-unit {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}

.coin-current {
  background: #f0fdf4;
  border-color: #bbf7d0;
}

.coin-current .detail-card-value {
  color: #16a34a;
}

.coin-total {
  background: #eff6ff;
  border-color: #bfdbfe;
}

.coin-total .detail-card-value {
  color: #2563eb;
}

/* ===== 封禁原因 ===== */
.detail-ban-reason {
  margin-top: 16px;
  padding: 12px 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
}

.ban-reason-label {
  font-size: 13px;
  font-weight: 600;
  color: #dc2626;
  margin-bottom: 4px;
}

.ban-reason-text {
  font-size: 14px;
  color: #991b1b;
  line-height: 1.5;
}

.dialog-footer {
  text-align: center;
}

/* ===== 设备风险详情 ===== */
.detail-risk-section {
  margin-top: 16px;
  padding: 16px;
  background: #fafafa;
  border: 1px solid #e8ecf1;
  border-radius: 8px;
}

.risk-section-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid #ebeef5;
}

.risk-section-title .el-icon-warning-outline {
  font-size: 16px;
  color: #e6a23c;
  margin-right: 6px;
}

.risk-items {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.risk-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 12px;
  background: #fff;
  border-radius: 6px;
  border: 1px solid #ebeef5;
  min-width: 180px;
  flex: 1;
}

.risk-item-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.risk-item-label {
  font-size: 13px;
  font-weight: 500;
  color: #303133;
  white-space: nowrap;
}

.risk-item-desc {
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: default;
}

.risk-raw-json {
  margin-top: 12px;
  border-top: 1px dashed #dcdfe6;
  padding-top: 10px;
}

.risk-raw-title {
  font-size: 12px;
  color: #909399;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  user-select: none;
}

.risk-raw-title:hover {
  color: #409eff;
}

.risk-json-content {
  margin-top: 8px;
  padding: 12px;
  background: #2d2d2d;
  color: #f8f8f2;
  border-radius: 6px;
  font-size: 12px;
  line-height: 1.6;
  overflow-x: auto;
  max-height: 300px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-all;
}

/* ===== 动画 ===== */
.main-card.animate__animated,
.data-table.animate__animated {
  --animate-duration: 0.3s;
}
</style>
